import { FieldPath, FieldPathValue, FieldValues, SetValueConfig, useForm } from "react-hook-form";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { UseAavoFormParams, UseAavoFormReturn } from "src/components/common/forms/useAavoForm/types.ts";

/**
 * Customized version of useForm from react-hook-form.
 * @param onSubmit Function to asynchronously convert TFieldValues to TResult.
 * Not called when input has errors.
 * May return "interrupted" to indicate that the submission was interrupted.
 * @param onCompleted Function to call when the form is successfully submitted or cancelled.
 * Not called when input has errors or onSubmit returns interruption.
 * Receives a FormResult with type "success" or "cancel" and the result value.
 * @param onFormEdited
 * @param otherParams Other useForm parameters.
 */
export function useAavoForm<TFieldValues extends FieldValues, TResult>({
	onCompleted,
	onFormEdited,
	onSubmit,
	...otherParams
}: UseAavoFormParams<TFieldValues, TResult>): UseAavoFormReturn<TFieldValues, TResult> {
	const { logErrorAndShowOnDialog } = useErrorDialog();

	const { resetField, setValue, watch, ...useFormReturnOther } = useForm<TFieldValues>({
		mode: "all",
		...otherParams,
	});

	if (onFormEdited)
		watch((_, { type }) => {
			if (type === "change") {
				onFormEdited();
			}
		});

	const useAavoFormReturn : UseAavoFormReturn<TFieldValues, TResult> = {
		...useFormReturnOther,
		resetField,
		watch,
		setValue: setValueCustom,
		triggerSubmit: (submitFunc) => triggerSubmit(submitFunc),
		triggerCancel: () => triggerCancel(),
	};

	return useAavoFormReturn;

	function setValueCustom<TFieldName extends FieldPath<TFieldValues>>(
		name: FieldPath<TFieldValues>,
		value: FieldPathValue<TFieldValues, TFieldName> | undefined | null,
		options?: SetValueConfig,
	) {
		if (value === undefined) resetField(name);
		else
			setValue(
				name,
				// TypeScripts doesn't allow null here, but it's sometimes required.
				// e.g. FormLazySelectField behaves differently with resetField and setValue(null).
				value as any,
				options,
			);
	}

	/**
	 * Triggers the form submission with the current input values.
	 * onSubmitFunc is a function that takes the form data and returns a FormSubmitResult.
	 * This may be overridden here by needs of e.g. AsyncForm.
	 *
	 * onSubmitFunc may return "interrupted" to indicate
	 * that the submission was interrupted and no further actions should be taken.
	 * Errors are caught and shown in the error dialog.
	 */
	async function triggerSubmit(onSubmitFunc: typeof onSubmit = onSubmit) {
		await useFormReturnOther.handleSubmit(
			async (formData: TFieldValues) => {
				try {
					const submitResult = await onSubmitFunc(formData, useAavoFormReturn);
					if (submitResult === "interrupted") {
						return;
					}
					await onCompleted?.({ type: "success", value: submitResult }, useAavoFormReturn);
				} catch (e) {
					logErrorAndShowOnDialog(e);
				}
			},
			(errors) => {
				console.info("Form has errors", errors);
			},
		)();
	}

	async function triggerCancel() {
		onCompleted?.({ type: "cancel", isEdited: useAavoFormReturn.formState.isDirty }, useAavoFormReturn);
	}
}
